import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { LayoutDashboard, GraduationCap, Building2, QrCode, BarChart3, ShieldCheck } from "lucide-react";
import SectionHeading from "../ui/SectionHeading";
import { fadeUp } from "../../utils/animations";

const dashboards = [
  { title: "Student LMS", copy: "Course progress, labs, certificates, assignments, and learning history in one view.", icon: GraduationCap },
  { title: "Enterprise Portal", copy: "Team enrollments, skill reports, cohort management, and corporate billing dashboard.", icon: Building2 },
  { title: "Talent Portal", copy: "Candidate profiles, placement tracking, recruitment requests, and hiring outcomes.", icon: LayoutDashboard },
  { title: "Certificate Verification", copy: "QR code scanning, blockchain anchoring, and public certificate verification portal.", icon: QrCode },
  { title: "Analytics Dashboard", copy: "Revenue, conversions, student growth, course performance, and placement metrics.", icon: BarChart3 },
  { title: "Admin Operations", copy: "Leads, content, users, settings, events, audit logs, and platform control.", icon: ShieldCheck },
];

const COLS_SM = 2;
const COLS_MD = 3;
const CELL_HEIGHT = 240; // fixed so every row lines up regardless of copy length

export default function DashboardsSection() {
  return (
    <Box component="section" sx={{ bgcolor: "#F8FAFC", py: { xs: 10, md: 14 } }}>
      <div className="section-shell">
        <SectionHeading
          eyebrow="Portals and dashboards"
          title="Role-specific workspaces for students, clients, talent partners, and administrators."
          copy="The platform blueprint includes LMS, enterprise portal, talent portal, certificate verification, analytics, and admin operations."
        />

        <Box sx={{ mt: { xs: 6, md: 8 } }}>
          <Grid container sx={{ borderTop: "1px solid rgba(15,23,42,0.10)" }}>
            {dashboards.map(({ title, copy, icon: Icon }, i) => {
              const colSm = i % COLS_SM;
              const colMd = i % COLS_MD;
              const rowSm = Math.floor(i / COLS_SM);
              const rowMd = Math.floor(i / COLS_MD);
              const isLastRowSm = rowSm === Math.floor((dashboards.length - 1) / COLS_SM);
              const isLastRowMd = rowMd === Math.floor((dashboards.length - 1) / COLS_MD);

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={title}
                  sx={{
                    borderLeft: {
                      sm: colSm !== 0 ? "1px solid rgba(15,23,42,0.10)" : "none",
                      md: colMd !== 0 ? "1px solid rgba(15,23,42,0.10)" : "none",
                    },
                    borderBottom: {
                      xs: "1px solid rgba(15,23,42,0.10)",
                      sm: isLastRowSm ? "none" : "1px solid rgba(15,23,42,0.10)",
                      md: isLastRowMd ? "none" : "1px solid rgba(15,23,42,0.10)",
                    },
                  }}
                >
                  <motion.div {...fadeUp(i * 0.04)} style={{ height: "100%" }}>
                    <Box
                      sx={{
                        height: "100%",
                        minHeight: CELL_HEIGHT,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        py: { xs: 3, md: 4 },
                        px: { xs: 3, md: 4 },
                        transition: "opacity 0.25s ease",
                        "&:hover": { opacity: 0.72 },
                        "&:hover .module-icon": { bgcolor: "primary.main", color: "white", borderColor: "primary.main" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box
                          className="module-icon"
                          sx={{
                            flexShrink: 0,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "white",
                            border: "2px solid",
                            borderColor: "primary.main",
                            color: "primary.main",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <Icon size={17} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "rgba(15,23,42,0.30)",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </Typography>
                      </Box>

                      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: "-0.01em", mb: 0.5 }}>
                          {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
                          {copy}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </div>
    </Box>
  );
}